/**
 * The workspace's **library of saved water profiles** — the brewer's own water
 * reports, plus the classic city analyses every brewing text works from.
 *
 * ## Why this is not `WaterProfileDoc`
 *
 * `./water.ts` already owns {@link WaterProfileDoc}: the six-ion analysis
 * *embedded in a recipe*, with no identity, no timestamps and no lifecycle. A
 * recipe must keep the numbers it was designed with even after the brewer
 * re-tests the tap, so the recipe's copy is a **value** and this one is a
 * **document**. Two different things, two different names:
 * {@link SavedWaterProfileDoc} is the library entry, and
 * {@link toWaterProfileDoc} is the one-way copy into a recipe. Nothing in a
 * recipe ever points back at a library id — that would reintroduce the drift
 * the snapshot rule exists to prevent (PLAN §6).
 *
 * ## The alkalinity entry form
 *
 * Storage is always **bicarbonate**, because HCO₃⁻ is an actual ion and
 * "alkalinity as CaCO₃" is a bookkeeping convention. But a water report is
 * printed one way or the other, and a brewer who typed `85 ppm as CaCO₃` and
 * comes back to find `104 ppm HCO₃` will reasonably think the app changed their
 * number. {@link SavedWaterProfileDoc.alkalinityEntryForm} records which box
 * they typed in so the form can show it back the way they entered it. It is a
 * *display* preference and never enters a calculation — exactly like
 * `settings.units`.
 *
 * ## Built-ins
 *
 * Seven ship, `builtin: true`, refused by `waterProfile.update` /
 * `waterProfile.delete`; the escape hatch is
 * `waterProfile.create({ from: builtinProfile })` — duplicate, then edit. Both
 * timestamps are `0` for the same reason the built-in equipment profiles' are:
 * these documents predate the workspace, and a wall clock in
 * {@link emptyWorkspace} would break replay determinism.
 */

import { z } from 'zod';
import { documentIdSchema, nameSchema, notesSchema, timestampSchema } from './common';
import type { WaterProfileDoc } from './water';

/** ppm (mg/L) of one ion, bounded by what a water report can physically read. */
const ionPpmSchema = z.number().finite().min(0).max(1000);

/**
 * A flavour ion the report may not print: `null` means **not measured**.
 *
 * Mirrors `./water`'s split exactly — sodium, chloride and sulfate may be
 * absent; calcium, magnesium and bicarbonate may not. See
 * {@link waterProfileDocSchema} there for why the line falls where it does.
 */
const optionalIonPpmSchema = ionPpmSchema.nullable();

/**
 * Which box on the form the brewer typed alkalinity into.
 *
 * Display only — storage is always bicarbonate. `caco3` means the report
 * printed "total alkalinity as CaCO₃"; `bicarbonate` means it printed HCO₃⁻.
 */
export const alkalinityEntryFormSchema = z.enum(['bicarbonate', 'caco3']);

/** @see alkalinityEntryFormSchema */
export type AlkalinityEntryForm = z.infer<typeof alkalinityEntryFormSchema>;

/** A saved water profile: one row of the workspace's water library. */
export const savedWaterProfileDocSchema = z.strictObject({
  /** Stable profile id, unique within the workspace. */
  id: documentIdSchema,
  /** Display name, e.g. `Burton on Trent` or `Tap — March 2026`. */
  name: nameSchema,
  /** True for the profiles this package ships; those are read-only. */
  builtin: z.boolean(),
  /** Brewer's prose: where the report came from, when it was taken. */
  notes: notesSchema,
  /** Ca²⁺, ppm. */
  calciumPpm: ionPpmSchema,
  /** Mg²⁺, ppm. */
  magnesiumPpm: ionPpmSchema,
  /** Na⁺, ppm, or `null` when the report does not print it. */
  sodiumPpm: optionalIonPpmSchema,
  /** Cl⁻, ppm, or `null` when the report does not print it. */
  chloridePpm: optionalIonPpmSchema,
  /** SO₄²⁻, ppm, or `null` when the report does not print it. */
  sulfatePpm: optionalIonPpmSchema,
  /** HCO₃⁻, ppm. Alkalinity as CaCO₃ = this × 0.820162. */
  bicarbonatePpm: ionPpmSchema,
  /** Measured pH of the water as drawn, or `null` for core's assumption (7.5). */
  ph: z.number().finite().min(3).max(11).nullable(),
  /** @see alkalinityEntryFormSchema */
  alkalinityEntryForm: alkalinityEntryFormSchema,
  /** Creation time, **ms since epoch**. */
  createdAt: timestampSchema,
  /** Last-modification time, **ms since epoch**. */
  updatedAt: timestampSchema,
});

/** @see savedWaterProfileDocSchema */
export type SavedWaterProfileDoc = z.infer<typeof savedWaterProfileDocSchema>;

/**
 * A library entry copied into the shape a recipe stores.
 *
 * One-way and lossy on purpose: identity, notes, the entry-form preference and
 * the timestamps are library bookkeeping and have no business inside a recipe.
 */
export function toWaterProfileDoc(profile: SavedWaterProfileDoc): WaterProfileDoc {
  return {
    name: profile.name,
    calciumPpm: profile.calciumPpm,
    magnesiumPpm: profile.magnesiumPpm,
    sodiumPpm: profile.sodiumPpm,
    chloridePpm: profile.chloridePpm,
    sulfatePpm: profile.sulfatePpm,
    bicarbonatePpm: profile.bicarbonatePpm,
    ph: profile.ph,
  };
}

/** The id of the ion-free profile: the dilution water, and a blank to build on. */
export const RO_WATER_PROFILE_ID = 'builtin-water-ro';

/** Everything a built-in profile shares, so the table below is only its numbers. */
function builtin(
  entry: Pick<
    SavedWaterProfileDoc,
    | 'id'
    | 'name'
    | 'notes'
    | 'calciumPpm'
    | 'magnesiumPpm'
    | 'sodiumPpm'
    | 'chloridePpm'
    | 'sulfatePpm'
    | 'bicarbonatePpm'
  >,
): SavedWaterProfileDoc {
  return {
    ...entry,
    builtin: true,
    ph: null,
    alkalinityEntryForm: 'bicarbonate',
    createdAt: 0,
    updatedAt: 0,
  };
}

/**
 * The shipped water profiles, in the order the picker should show them.
 *
 * RO first because it is the one every brewer actually uses, then the classic
 * city analyses roughly in the order a brewer meets them — pale and hoppy
 * first, dark and alkaline last. It is not a strict residual-alkalinity sort:
 * Vienna's published analysis carries so much calcium that its RA is the
 * *lowest* of the set, and burying it between Pilsen and Burton would be sorted
 * but useless.
 *
 * **Provenance.** Pilsen, Dublin, Burton and Munich are byte-identical to
 * `@beerwright/core`'s golden fixtures in `src/fixtures/water-profiles.ts`,
 * where each one's alkalinity and Kolbach residual alkalinity are computed
 * longhand and pinned by the engine's own tests. London and Vienna come from
 * the same source those fixtures cite — Palmer & Kaminski, *Water* (2013),
 * appendix A, the historical analyses reproduced by essentially every brewing
 * calculator — and their arithmetic is written out below to the same standard.
 * These are historical/typical analyses, **not** this morning's utility report;
 * they are here because they are the numbers every reference works from, which
 * makes them checkable.
 *
 * | Profile | Ca | Mg | Na | Cl | SO₄ | HCO₃ | Alk as CaCO₃ | RA |
 * | --- | --- | --- | --- | --- | --- | --- | --- | --- |
 * | RO / distilled | 0 | 0 | 0 | 0 | 0 | 0 | 0 | 0 |
 * | Pilsen | 7 | 2 | 2 | 5 | 5 | 15 | 12.3 | 6.1 |
 * | Burton on Trent | 295 | 45 | 55 | 25 | 725 | 300 | 246.0 | 9.1 |
 * | Vienna | 200 | 60 | 8 | 12 | 125 | 120 | 98.4 | −79.6 |
 * | London | 52 | 32 | 86 | 34 | 32 | 104 | 85.3 | 29.4 |
 * | Munich | 76 | 18 | 2 | 2 | 10 | 200 | 164.0 | 99.2 |
 * | Dublin | 118 | 4 | 12 | 19 | 54 | 319 | 261.6 | 175.1 |
 *
 * The two rows core does not already pin, worked longhand with the same
 * constants (Alk = HCO₃ × 0.820162; Ca hardness = Ca × 2.4973; Mg hardness =
 * Mg × 4.1179; RA = Alk − Ca hardness/3.5 − Mg hardness/7):
 *
 * ```
 * London  Alk = 104 × 0.820162 = 85.297
 *         Ca hardness = 52 × 2.4973 = 129.86 → /3.5 = 37.10
 *         Mg hardness = 32 × 4.1179 = 131.77 → /7   = 18.82
 *         RA  = 85.30 − 37.10 − 18.82 = 29.38
 * Vienna  Alk = 120 × 0.820162 = 98.419
 *         Ca hardness = 200 × 2.4973 = 499.46 → /3.5 = 142.70
 *         Mg hardness = 60 × 4.1179 = 247.07 → /7   = 35.30
 *         RA  = 98.42 − 142.70 − 35.30 = −79.58
 * ```
 *
 * Vienna's negative residual alkalinity is not a typo: the reproduced analysis
 * carries 200 ppm calcium against only 120 ppm bicarbonate, so the mineral
 * offset more than cancels the alkalinity. It is left as published rather than
 * tuned, which is the whole point of shipping citable numbers.
 */
export const BUILTIN_WATER_PROFILES: readonly SavedWaterProfileDoc[] = [
  builtin({
    id: RO_WATER_PROFILE_ID,
    name: 'RO / distilled',
    notes:
      'Nothing dissolved in it, so every ion reads zero. Build a target up from here with salts, or blend it with your tap water to dilute it.',
    calciumPpm: 0,
    magnesiumPpm: 0,
    sodiumPpm: 0,
    chloridePpm: 0,
    sulfatePpm: 0,
    bicarbonatePpm: 0,
  }),
  builtin({
    id: 'builtin-water-pilsen',
    name: 'Pilsen',
    notes:
      'Very soft, with barely more mineral than RO. Residual alkalinity is 6 ppm, which suits pale, delicately hopped lager and not much else.',
    calciumPpm: 7,
    magnesiumPpm: 2,
    sodiumPpm: 2,
    chloridePpm: 5,
    sulfatePpm: 5,
    bicarbonatePpm: 15,
  }),
  builtin({
    id: 'builtin-water-burton',
    name: 'Burton on Trent',
    notes:
      'Very high in sulfate (725 ppm), which gives English pale ale its dry, firm bitterness. Alkalinity is high too, but the calcium cancels most of it, leaving a residual alkalinity of only 9 ppm.',
    calciumPpm: 295,
    magnesiumPpm: 45,
    sodiumPpm: 55,
    chloridePpm: 25,
    sulfatePpm: 725,
    bicarbonatePpm: 300,
  }),
  builtin({
    id: 'builtin-water-vienna',
    name: 'Vienna',
    notes:
      "Hard and high in calcium. There's more calcium than the alkalinity can offset, so residual alkalinity comes out negative and a mash with this water needs no acid.",
    calciumPpm: 200,
    magnesiumPpm: 60,
    sodiumPpm: 8,
    chloridePpm: 12,
    sulfatePpm: 125,
    bicarbonatePpm: 120,
  }),
  builtin({
    id: 'builtin-water-london',
    name: 'London',
    notes:
      'Moderately alkaline and unusually high in sodium, which is part of why London porter and mild read as round rather than crisp.',
    calciumPpm: 52,
    magnesiumPpm: 32,
    sodiumPpm: 86,
    chloridePpm: 34,
    sulfatePpm: 32,
    bicarbonatePpm: 104,
  }),
  builtin({
    id: 'builtin-water-munich',
    name: 'Munich',
    notes:
      "Residual alkalinity is 99 ppm. That's too alkaline for a pale mash without acid, but right for the amber Munich-malt beers the city is known for.",
    calciumPpm: 76,
    magnesiumPpm: 18,
    sodiumPpm: 2,
    chloridePpm: 2,
    sulfatePpm: 10,
    bicarbonatePpm: 200,
  }),
  builtin({
    id: 'builtin-water-dublin',
    name: 'Dublin',
    notes:
      'Residual alkalinity is 175 ppm, the highest of the classic waters. Only a dark, acidic grist balances it, which is why it suits stout.',
    calciumPpm: 118,
    magnesiumPpm: 4,
    sodiumPpm: 12,
    chloridePpm: 19,
    sulfatePpm: 54,
    bicarbonatePpm: 319,
  }),
];
